<template>
  <div>
    <!-- Data Table Section -->
    <v-data-table-server
      :headers="headers"
      :items="listData.users"
      :items-length="pagination.totalItems"
      :loading="listData.loading"
      :items-per-page="pagination.itemsPerPage"
      :items-per-page-options="pagination.perPageOptions"
      v-model:page="pagination.currentPage"
      v-model:items-per-page="pagination.itemsPerPage"
      @update:options="$emit('load-items', $event)"
      mobile-breakpoint="sm"
      class="elevation-0 data-table-modern"
    >

          <template v-slot:loading>
            <v-skeleton-loader class="mx-auto" max-width="100%" type="table-row@3"></v-skeleton-loader>
          </template>
          <template v-slot:loader>
          </template>

          <template v-slot:no-data>
            <div v-if="listData.error" class="text-center py-8">
              <v-icon size="64" color="danger-color" class="mb-4">mdi-alert-circle</v-icon>
              <h3 class="text-title-large text-danger-color mb-2">Error Loading Data</h3>
              <p class="text-body-medium text-grey-darken-1 mb-4">
                {{ listData.message || 'Failed to load archived users. Please try again.' }}
              </p>
              <v-btn color="danger-color" variant="outlined" @click="$emit('load-items', {})">
                <v-icon left>mdi-refresh</v-icon>
                Retry
              </v-btn>
            </div>
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-account-off</v-icon>
              <h3 class="text-title-large text-grey-darken-1 mb-2">No Accounts for Deletion</h3>
              <p class="text-body-medium text-grey-darken-1 mb-0">
                There are currently no user accounts scheduled for deletion.
              </p>
            </div>
          </template>

          <template v-slot:[`item.serial_no`]="{ item }">
            {{ ((pagination.currentPage - 1) * pagination.itemsPerPage) + listData.users.indexOf(item) + 1 }}
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <v-badge :color="item.active ? 'success' : 'error'" dot class="cursor-pointer">
              <v-tooltip location="bottom">
                <template v-slot:activator="{ props }">
                  <div v-bind="props">
                    <v-icon icon="mdi-account" size="default" :color="item.active ? 'success' : 'error'"></v-icon>
                  </div>
                </template>
                <span>{{ item.active ? 'Active User' : 'Suspended User' }}</span>
              </v-tooltip>
            </v-badge>
            &nbsp;
            <!-- Opens the user page, read-only while the account is up for deletion; admin accounts can't be opened. -->
            <router-link v-if="!isAdminUser(item)" class="text-brand-color" :to="{ name: 'users.edit', params: { id: item.id } }">
              {{ item.name }}
            </router-link>
            <span v-else>{{ item.name }}</span>
          </template>

          <template v-slot:[`item.email`]="{ item }">
            <div class="d-flex align-start ga-2">
              <v-badge :color="item.verified ? 'success' : 'warning'" dot class="cursor-pointer mt-1">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <div v-bind="props">
                      <v-icon icon="mdi-email" size="default" :color="item.verified ? 'success' : 'warning'"></v-icon>
                    </div>
                  </template>
                  <span>{{ item.verified ? 'Verified' : 'Unverified' }}</span>
                </v-tooltip>
              </v-badge>
              <span class="email-cell" :title="item.email">{{ item.email }}</span>
            </div>
          </template>

          <template v-slot:[`item.subscription`]="{ item }">
            <span class="subscription">
              {{ subscriptionMethods.getUserSubscriptionTitle(item) }}
              <template v-if="subscriptionMethods.getUserSubscriptionStatus(item)">
                <br>
                ({{ subscriptionMethods.getUserSubscriptionStatus(item) }})
              </template>
            </span>
          </template>

          <template v-slot:[`item.role`]="{ item }">
            <v-chip :color="getRoleChipColor(item.roles)" variant="tonal" size="small" rounded="lg"
              class="font-weight-medium">
              {{ userRolesMethod(item.roles) }}
            </v-chip>
          </template>

          <template v-slot:[`item.forms_count`]="{ item }">
            <v-chip color="brand-color" variant="tonal" rounded="pill">{{ item.forms_count || 0 }}</v-chip>
          </template>

          <template v-slot:[`item.user_leads_count`]="{ item }">
            <v-chip color="info" variant="tonal" rounded="pill">{{ item.user_leads_count || 0 }}</v-chip>
          </template>

          <template v-slot:[`item.delete_at`]="{ item }">
            {{ localeDate(item.inactive_details?.delete_at || item.delete_at) }}
          </template>

          <template v-slot:[`item.delete_source`]="{ item }">
            <v-chip :color="getDeletionSourceColor(item.inactive_details?.delete_source)" size="small" label
              class="my-2">
              {{ getDeletionSourceText(item.inactive_details?.delete_source) }}
            </v-chip>
          </template>

    </v-data-table-server>

  </div>
</template>

<script>
import helperMixin from '@/mixins/helper'
import userMixin from '@/mixins/user'
import { mapGetters } from 'vuex'
import roleTypes from '@/store/roles'
import { DELETE_SOURCE_DISPLAY } from '@/shared/constants/user'

export default {
  mixins: [helperMixin, userMixin],
  props: {
    pagination: {
      type: Object,
      required: true
    },
    headers: {
      type: Array,
      required: true
    },
    listData: {
      type: Object,
      required: true
    },
    userRolesMethod: {
      type: Function,
      required: true
    },
    subscriptionMethods: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters(['forms', 'token', 'user']),

    hasBasePermissions() {
      if (this.isAdmin || this.isSuperCustomer) {
        return true
      }
      const restrictedStates = [
        (this.userMixin_hasActiveFreeTrialPlan && (!this.userMixin_inTrial || this.userMixin_planExpired)),
        this.userMixin_hasActiveFreePlan,
        this.userMixin_isPastDueUser,
        (this.userMixin_hasPausedSubscription && !this.userMixin_hasPausedSubscriptionInGrace)
      ]
      return !restrictedStates.some(state => state === true)
    }
  },
  methods: {
    getDeletionSourceText(source) {
      return DELETE_SOURCE_DISPLAY[source]?.text || source || 'Unknown'
    },
    getDeletionSourceColor(source) {
      return DELETE_SOURCE_DISPLAY[source]?.color || 'grey-darken-3'
    },
    isAdminUser(user) {
      return !!user.roles?.some(role => role.name === roleTypes.ADMIN)
    }
  }
}
</script>
<style scoped>
.email-cell {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;
}
</style>
